import { sleep } from '@flowus/common/async';
import { getSessionStorage, SessionKeys } from '@flowus/common/storage';
import type { ErrorHandler, RequestFactory } from '@next-space/fe-api-idl';
import { createRequest } from '@next-space/fe-api-idl';
import type { AxiosRequestConfig, InternalAxiosRequestConfig } from 'axios';
import _Axios from 'axios';
import { VITE_PREFIX } from 'src/env';
import { ViewPath } from 'src/utils';
import { isFlowUsApp } from 'src/utils/electron-util';
import { getToken } from 'src/utils/get-next-auth';
import { message } from '../components/message';
import { currentVersion, TOKEN, USER_ID } from '../const';
import { checkViewPath, getDomain } from '../utils/url-utils';
import { SENDER_ID } from './axios';

const baseURL = isFlowUsApp.check && !__HOST_LOCAL__ ? `${VITE_PREFIX}/api` : '/api';

const axios = _Axios.create({
  baseURL,
  validateStatus() {
    return true;
  },
});

axios.interceptors.request.use((config) => {
  const token = getToken();
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }

  config.headers['X-SENDER-ID'] = SENDER_ID;
  config.headers['x-platform'] = isFlowUsApp.check ? 'desktop' : 'web-cookie';
  config.headers['x-product'] = __BUILD_IN__ ? 'buildin' : 'flowus';
  config.headers['app_version_name'] = currentVersion;
  config.headers['x-app-origin'] = 'web';
  if (__BUILD_IN__) {
    config.headers['Accept-Language'] = 'en-US,en;q=0.9,zh;q=0.8';
  }

  const psw = getSessionStorage(SessionKeys.PASSWORD);
  if (psw) {
    config.headers['share-password'] = psw;
  }

  return config;
});

axios.interceptors.response.use((res: any) => {
  if (res.status === 401 || (res.data && res.data.code === 401)) {
    // 补丁，只有这个接口不自动跳/login
    if (res.config.url === '/users/me') {
      return res;
    }

    localStorage.removeItem(USER_ID);
    localStorage.removeItem(TOKEN);

    if (!checkViewPath(ViewPath.login)) {
      // 未登录也可以访问公开主页
      if (window.location.pathname === ViewPath.main && getDomain()) {
        return res;
      }
      if (checkViewPath(ViewPath.form, 'startsWith')) {
        return;
      }

      window.location.pathname = ViewPath.login;
    }
  } else {
    const token = res.headers['x-set-token'];
    if (token) {
      localStorage.setItem(TOKEN, token);
    }
  }

  return res;
});

axios.interceptors.request.use((config: InternalAxiosRequestConfig<AxiosRequestConfig>) => {
  let { url = '' } = config;
  const { method, params } = config;
  if (method === 'get' && params) {
    url += '?';
    Object.keys(params).forEach((k) => {
      const v = params[k];
      if (v !== undefined) {
        url += `${k}=${encodeURIComponent(v)}&`;
      }
    });
    config.url = url.slice(0, url.length - 1);
    config.params = undefined;
  }
  return config;
});

const factory: RequestFactory = async (options) => {
  const { method, path, headers, query, formData, body } = options;
  const url = path.replace(/[:]/g, '_');

  let data = body;
  if (formData) {
    data = new FormData();
    Object.entries(data).forEach(([k, v]) => {
      // undefined or null
      if (v != null) {
        data.append(k, v);
      }
    });
  }

  const res = await axios({
    method,
    url,
    params: query,
    data,
    headers,
  });

  if (res.data.code !== 200) {
    // eslint-disable-next-line no-console
    console.error(res.data);
  }

  return {
    url,
    ok: true,
    status: res.status,
    statusText: res.statusText,
    body: res.data,
  };
};

/**
 * 通过 {API}.raw() 调用的方法不会触发 onError
 */
const onError: ErrorHandler = (res) => {
  // eslint-disable-next-line no-console
  console.log('request 业务错误', res);

  if (res.code === 502) {
    return;
  }
  const msg: string = String(res.msg || (res as any).message).toLocaleLowerCase();

  if (/服务器内部错误|数据库锁超时|internal server error/.test(msg) && __HOST_PRODUCTION__) {
    return;
  }

  if (msg) {
    message.error({
      key: 'msg',
      content: msg,
    });
  }

  // 报错后 5 秒关闭所有 message。以免出现卡 loading 的情况
  // 先获取是因为避免关闭后来新弹出的 message
  const keys = message.getAll();
  void sleep(5000).then(() => {
    keys.forEach(message.closeMessage);
  });
};

export const request = createRequest(factory, onError);
